import { create } from 'zustand';
import { api, type FriendRow, type FriendRequestRow } from '../lib/api';
import { getSocket } from '../lib/socket';

interface State {
  friends: FriendRow[];
  incoming: FriendRequestRow[];
  outgoing: FriendRequestRow[];
  loading: boolean;
  /** Has the data been loaded at least once this session? */
  loaded: boolean;
  /** Fetch friends + requests fresh from the server. */
  refresh: () => Promise<void>;
  /** Subscribe to live friend events. Returns an unsubscribe function. */
  attach: () => () => void;
  /** Mark a single friend online/offline locally. */
  setOnline: (userId: string, online: boolean) => void;
  reset: () => void;
}

export const useFriends = create<State>((set, get) => ({
  friends: [],
  incoming: [],
  outgoing: [],
  loading: false,
  loaded: false,

  reset: () => set({ friends: [], incoming: [], outgoing: [], loading: false, loaded: false }),

  refresh: async () => {
    set({ loading: true });
    try {
      const [friends, reqs] = await Promise.all([
        api.friends(),
        api.friendRequests(),
      ]);
      set({ friends, incoming: reqs.incoming, outgoing: reqs.outgoing, loaded: true });
    } catch {
      // ignore — caller can show stale data
    } finally {
      set({ loading: false });
    }
  },

  setOnline: (userId, online) => {
    set({
      friends: get().friends.map(f => f.id === userId ? { ...f, online } : f),
    });
  },

  attach: () => {
    const s = getSocket();
    const onOnline = (p: { userId: string }) => get().setOnline(p.userId, true);
    const onOffline = (p: { userId: string }) => get().setOnline(p.userId, false);
    const onRequest = () => { get().refresh(); };
    const onResponded = () => { get().refresh(); };
    const onRemoved = () => { get().refresh(); };
    s.on('friend:online', onOnline);
    s.on('friend:offline', onOffline);
    s.on('friend:request', onRequest);
    s.on('friend:requestResponded', onResponded);
    s.on('friend:removed', onRemoved);
    return () => {
      s.off('friend:online', onOnline);
      s.off('friend:offline', onOffline);
      s.off('friend:request', onRequest);
      s.off('friend:requestResponded', onResponded);
      s.off('friend:removed', onRemoved);
    };
  },
}));
